import React, { useEffect, useMemo, useState } from "react";
import { z } from "zod/v4";

import styles from "./SnapTradePortal.module.css";

const z_SnapTradePortalMessage = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("SUCCESS"),
    authorizationId: z.string().min(1),
  }),
  z.object({
    status: z.literal("ERROR"),
    detail: z.string().optional(),
    errorCode: z.string().optional(),
  }),
  z.object({ status: z.literal("CLOSE_MODAL") }),
]);

function isAllowedSnapTradeOrigin(origin: string): boolean {
  try {
    const url = new URL(origin);
    if (url.hostname === "localhost" && url.port === "5173") return true;
    return (
      url.protocol === "https:" &&
      (url.hostname === "snaptrade.com" ||
        url.hostname.endsWith(".snaptrade.com"))
    );
  } catch {
    return false;
  }
}

export type T_SnapTradePortalProps = {
  loginLink: string;
  onCancel: () => void;
  onError: (message: string) => void;
  onLoaded?: () => void;
  onSuccess: (authorizationId: string) => void;
};

export function SnapTradePortal({
  loginLink,
  onCancel,
  onError,
  onLoaded,
  onSuccess,
}: T_SnapTradePortalProps) {
  const [loaded, setLoaded] = useState(false);
  const portalUrl = useMemo(() => {
    try {
      const url = new URL(loginLink);
      url.searchParams.set("customIframe", "1.0.0");
      return url.toString();
    } catch {
      return loginLink;
    }
  }, [loginLink]);

  // eslint-disable-next-line no-restricted-syntax -- SnapTrade reports the external iframe lifecycle through postMessage.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!isAllowedSnapTradeOrigin(event.origin)) return;

      if (typeof event.data === "string") {
        if (event.data.startsWith("SUCCESS:")) {
          const authorizationId = event.data.slice("SUCCESS:".length);
          if (authorizationId) onSuccess(authorizationId);
          return;
        }
        if (event.data === "SUCCESS") {
          onSuccess("SUCCESS");
          return;
        }
        if (
          event.data === "CLOSED" ||
          event.data === "ABANDONED" ||
          event.data === "CLOSE_MODAL"
        ) {
          onCancel();
        }
        return;
      }

      const parsed = z_SnapTradePortalMessage.safeParse(event.data);
      if (!parsed.success) return;
      if (parsed.data.status === "SUCCESS") {
        onSuccess(parsed.data.authorizationId);
      } else if (parsed.data.status === "ERROR") {
        onError(
          parsed.data.detail ??
            parsed.data.errorCode ??
            "The brokerage portal reported an error.",
        );
      } else {
        onCancel();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [onCancel, onError, onSuccess]);

  return (
    <div className={styles.portal} data-snaptrade-portal>
      {!loaded ? (
        <div className={styles.loading} role="status">
          <span aria-hidden className="lk-spinner" />
          <span>Loading connection portal</span>
        </div>
      ) : null}
      <div className={styles.frameShell}>
        <iframe
          allow="clipboard-write"
          className={styles.frame}
          onError={() => onError("The brokerage portal could not be loaded.")}
          onLoad={() => {
            if (!loaded) onLoaded?.();
            setLoaded(true);
          }}
          src={portalUrl}
          title="SnapTrade connection portal"
        />
      </div>
    </div>
  );
}
